import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Upload,
  Checkbox,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { TRAINING_TYPE, ACCOUNT_TYPE } from "utils/consts";
import "components/css/Training.scss";

const { Option } = Select;

const ALL_MENTORS_VALUE = "__ALL_MENTORS__";
const ALL_MENTEES_VALUE = "__ALL_MENTEES__";

const extractIdValue = (item) => {
  if (!item) return null;
  if (item._id && item._id.$oid) return item._id.$oid;
  if (item.id && item.id.$oid) return item.id.$oid;
  return item.id ?? item;
};

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

// TODO: Change the weird names of some of the forms like typee and filee
function UpdateTrainingModal({
  onCancel,
  onFinish,
  open,
  currentTraining,
  loading,
  hubOptions,
  partnerOptions,
  menteeOptions,
  mentorOptions,
}) {
  const [form] = Form.useForm();
  const [trainingType, setTrainingType] = useState("");
  const [isNewDocument, setIsNewDocument] = useState(false);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [, setRole] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [mentees, setMentees] = useState([]);
  const newTraining = !currentTraining;

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues.typee) {
      setTrainingType(changedValues.typee);
    } else if (changedValues.document) {
      setIsNewDocument(true);
    }
    setValuesChanged(true);
  };

  const onOk = () => {
    if (valuesChanged) {
      form
        .validateFields()
        .then((values) => {
          values.document = values.document?.[0]?.originFileObj;
          onFinish(
            {
              ...values,
              isNewDocument,
              isVideo: values.typee !== TRAINING_TYPE.DOCUMENT,
            },
            newTraining
          );
        })
        .catch((info) => {
          console.error("Validate Failed:", info);
        });
    } else {
      onCancel();
    }
  };

  useEffect(() => {
    form.resetFields();
    setValuesChanged(false);
    setIsNewDocument(false);
    setTrainingType(TRAINING_TYPE.VIDEO);
    if (currentTraining) {
      setTrainingType(currentTraining.typee);
      currentTraining.role = parseInt(currentTraining.role);
      form.setFieldsValue(currentTraining);
      if (currentTraining.role === ACCOUNT_TYPE.MENTOR) {
        if (currentTraining.mentor_id && currentTraining.mentor_id.length > 0) {
          setMentors(mentorOptions);
        }
      }
      if (currentTraining.role === ACCOUNT_TYPE.MENTEE) {
        if (currentTraining.mentee_id && currentTraining.mentee_id.length > 0) {
          setMentees(menteeOptions);
        }
      }
      if (currentTraining.role === ACCOUNT_TYPE.PARTNER) {
        if (currentTraining.partner_id) {
          var partner_data = partnerOptions.find(
            (x) => x.value === currentTraining.partner_id
          );
          if (partner_data) {
            setMentees(partner_data.assign_mentees);
            setMentors(partner_data.assign_mentors);
          }
        }
      }

      if (currentTraining.typee === TRAINING_TYPE.DOCUMENT) {
        form.setFieldValue("document", [
          {
            name: currentTraining.file_name,
          },
        ]);
      }
    } else {
      setMentors([]);
      setMentees([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentTraining]);

  const setMentorMentees = (partner_id) => {
    form.setFieldValue("mentor_id", null);
    form.setFieldValue("mentee_id", null);
    var partner_data = partnerOptions.find((x) => x.value === partner_id);
    if (partner_data) {
      setMentees(partner_data.assign_mentees);
      setMentors(partner_data.assign_mentors);
      let default_data = [];
      partner_data.assign_mentees.map((item) => {
        default_data.push(item.id.$oid ? item.id.$oid : item.id);
        return true;
      });
      form.setFieldValue("mentee_id", default_data);
      default_data = [];
      partner_data.assign_mentors.map((item) => {
        default_data.push(item.id.$oid ? item.id.$oid : item.id);
        return true;
      });
      form.setFieldValue("mentor_id", default_data);
    }
  };

  const changeRole = (val) => {
    setRole(val);
    form.setFieldValue("partner_id", "");
    form.setFieldValue("hub_id", "");
    form.setFieldValue("mentor_id", null);
    form.setFieldValue("mentee_id", null);
    setMentees([]);
    setMentors([]);
    if (val === ACCOUNT_TYPE.MENTEE) {
      setMentees(menteeOptions);
      const allMenteeIds = (menteeOptions || [])
        .map((item) => extractIdValue(item))
        .filter(Boolean);
      form.setFieldValue("mentee_id", allMenteeIds);
    }
    if (val === ACCOUNT_TYPE.MENTOR) {
      setMentors(mentorOptions);
      const allMentorIds = (mentorOptions || [])
        .map((item) => extractIdValue(item))
        .filter(Boolean);
      form.setFieldValue("mentor_id", allMentorIds);
    }
  };

  const handleMentorSelectChange = (selectedValues) => {
    if (selectedValues?.includes(ALL_MENTORS_VALUE)) {
      const allMentorIds = (mentors || [])
        .map((item) => extractIdValue(item))
        .filter(Boolean);
      form.setFieldValue("mentor_id", allMentorIds);
    } else {
      form.setFieldValue("mentor_id", selectedValues);
    }
    setValuesChanged(true);
  };

  const handleMenteeSelectChange = (selectedValues) => {
    if (selectedValues?.includes(ALL_MENTEES_VALUE)) {
      const allMenteeIds = (mentees || [])
        .map((item) => extractIdValue(item))
        .filter(Boolean);
      form.setFieldValue("mentee_id", allMenteeIds);
    } else {
      form.setFieldValue("mentee_id", selectedValues);
    }
    setValuesChanged(true);
  };

  return (
    <Modal
      title="Training Editor"
      onOk={onOk}
      onCancel={onCancel}
      open={open}
      confirmLoading={loading}
    >
      <Form
        form={form}
        onValuesChange={handleValuesChange}
        layout="vertical"
        className="update-training-form"
      >
        <Form.Item
          name="name"
          label="Training Name"
          rules={[
            {
              required: true,
              max: 100,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="typee"
          label="Training Type"
          initialValue={TRAINING_TYPE.VIDEO}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Radio.Group>
            <Radio value={TRAINING_TYPE.VIDEO}>Video</Radio>
            <Radio value={TRAINING_TYPE.DOCUMENT}> Document </Radio>
            <Radio value={TRAINING_TYPE.LINK}> External Link </Radio>
          </Radio.Group>
        </Form.Item>
        {form.getFieldValue("typee") === TRAINING_TYPE.DOCUMENT && (
          <Form.Item
            rules={[
              {
                required: false,
              },
            ]}
            name="requried_sign"
            valuePropName="checked"
          >
            <Checkbox>Required Sign</Checkbox>
          </Form.Item>
        )}
        {newTraining && (
          <Form.Item
            rules={[
              {
                required: false,
              },
            ]}
            name="send_notification"
            valuePropName="checked"
          >
            <Checkbox>Send Notification</Checkbox>
          </Form.Item>
        )}
        {trainingType === TRAINING_TYPE.DOCUMENT ? (
          <Form.Item
            name="document"
            label="Document"
            rules={[
              {
                required: true,
              },
            ]}
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload accept=".pdf" maxCount={1} beforeUpload={() => false}>
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
        ) : (
          <Form.Item
            name="url"
            label="URL"
            rules={[
              {
                required: true,
                type: "url",
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
        <Form.Item
          name="description"
          label="Training Description"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>
        <Form.Item
          name="role"
          label="Role"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select
            onChange={(val) => {
              changeRole(val);
            }}
          >
            <Option value={ACCOUNT_TYPE.MENTOR}>Mentor</Option>
            <Option value={ACCOUNT_TYPE.MENTEE}>Mentee</Option>
            <Option value={ACCOUNT_TYPE.PARTNER}>Partner</Option>
            <Option value={ACCOUNT_TYPE.HUB}>Hub</Option>
          </Select>
        </Form.Item>
        {form.getFieldValue("role") === ACCOUNT_TYPE.PARTNER && (
          <Form.Item
            name="partner_id"
            label="Partner"
            rules={[
              {
                required: true,
                message: "Please select a partner",
              },
            ]}
          >
            <Select
              onChange={(partner_id) => setMentorMentees(partner_id)}
              placeholder="Select a partner"
              showSearch
              filterOption={(input, option) =>
                (option.children || "")
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value={""}></Option>
              {partnerOptions.map((item) => {
                return <Option value={item.value}>{item.label}</Option>;
              })}
            </Select>
          </Form.Item>
        )}
        {mentors && mentors.length > 0 && (
          <>
            <Form.Item
              name="mentor_id"
              label="Mentor"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Please select at least one mentor",
                },
              ]}
            >
              <Select
                mode="multiple"
                onChange={handleMentorSelectChange}
                allowClear
                placeholder="Select mentors for this training"
                showSearch
                filterOption={(input, option) =>
                  (option.children || "")
                    .toString()
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={ALL_MENTORS_VALUE}>Select All Mentors</Option>
                {mentors.map((item) => {
                  return (
                    <Option
                      value={
                        item._id
                          ? item._id.$oid
                          : item.id.$oid
                          ? item.id.$oid
                          : item.id
                      }
                    >
                      {item.name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </>
        )}
        {mentees && mentees.length > 0 && (
          <>
            <Form.Item
              name="mentee_id"
              label="Mentee"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Please select at least one mentee",
                },
              ]}
            >
              <Select
                mode="multiple"
                onChange={handleMenteeSelectChange}
                allowClear
                placeholder="Select mentees for this training"
                showSearch
                filterOption={(input, option) =>
                  (option.children || "")
                    .toString()
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={ALL_MENTEES_VALUE}>Select All Mentees</Option>
                {mentees.map((item) => {
                  return (
                    <Option
                      value={
                        item._id
                          ? item._id.$oid
                          : item.id.$oid
                          ? item.id.$oid
                          : item.id
                      }
                    >
                      {item.name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </>
        )}
        {form.getFieldValue("role") === ACCOUNT_TYPE.HUB && (
          <Form.Item
            name="hub_id"
            label="Hub"
            rules={[
              {
                required: true,
                message: "Please select a hub",
              },
            ]}
          >
            <Select
              placeholder="Select a hub"
              showSearch
              filterOption={(input, option) =>
                (option.children || "")
                  .toString()
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value={""}></Option>
              {hubOptions.map((item) => {
                return <Option value={item.value}>{item.label}</Option>;
              })}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default UpdateTrainingModal;
